// Turns a (ChartSpec, ChartResult) pair into the props each @bi/shared chart
// primitive needs. `ChartResult` is flat and viz_type-agnostic on the wire
// (`service/bi/core/execute.py`), so every function reads it against the
// chart's own declared `ChartQuery`.
//
// SELECT-list order (`service/bi/core/compiler.py` `compile_chart_query`): for
// every viz_type except a raw-mode `table`, dimensions come first (each
// `query.groupby` entry, in order, then AT MOST ONE bucket column: a
// `DATE_TRUNC` bucket when a grain applies, else a bare `x_axis` not already in
// `groupby`), then one metric column per `metricRefs(query)` entry, in declared
// order. A raw-mode table's SELECT list is `query.all_columns` verbatim.
//
// Read POSITIONALLY, never by alias: `_safe_alias` slugifies a metric's
// free-text `label`, so matching by name would duplicate that rule. Position is
// what the compiler guarantees.
import { resolveEdges } from "@bi/shared";
import type { ChartSeries, DonutSlice, FunnelStageDatum, FunnelTransitionDatum } from "@bi/shared";
import type {
  ChartQuery,
  ChartResult,
  ChartSpec,
  FunnelDisplay,
  GaugeDisplay,
  MetricSpec,
} from "@bi/shared/types";
import { layoutFunnelFlow } from "./funnel-flow";

/** `query.metrics` if populated, else the single `query.metric` wrapped in a
 *  list, else empty: the same "metrics-or-metric" fallback `compiler.py`
 *  applies before building the SELECT list. Returns `MetricSpec` (a `MetricRef`
 *  or a `SimpleMetric`). Readers here use only `label`, which both carry. */
export function metricRefs(query: ChartQuery): MetricSpec[] {
  if (query.metrics && query.metrics.length > 0) return query.metrics;
  return query.metric ? [query.metric] : [];
}

/** True iff the query DECLARES one extra dimension column past `groupby`: a
 *  time bucket (`time.time_grain_sqla` set) or a bare `x_axis` not already in
 *  `groupby`. This is only half the compiler's condition (the bucket also needs
 *  a resolvable temporal column), so index with `resolveDimensionCount`. */
export function hasBucketColumn(query: ChartQuery): boolean {
  if (query.time?.time_grain_sqla) return true;
  const groupby = query.groupby ?? [];
  return Boolean(query.x_axis) && !groupby.includes(query.x_axis as string);
}

/** How many leading columns in `ChartResult.columns`/each row are dimensions
 *  (groupby, plus the one optional bucket/x_axis column) rather than
 *  metrics. Everything from this index onward is a metric column, in
 *  `metricRefs(query)` order. */
export function dimensionColumnCount(query: ChartQuery): number {
  const groupby = query.groupby ?? [];
  return groupby.length + (hasBucketColumn(query) ? 1 : 0);
}

/** The dimension count to actually index with: the compiler's own, when the
 *  result carries it.
 *
 *  `dimensionColumnCount` above re-derives the index from the ChartQuery, and
 *  its `hasBucketColumn` mirrors only HALF of the compiler's condition — the
 *  compiler emits the DATE_TRUNC bucket when `time_grain_sqla` is set AND a
 *  temporal column resolves. For a chart with a grain and no resolvable
 *  temporal column (reachable, since `time_range: "No filter"` means nothing
 *  raises `MissingTemporalColumn`) the compiler emits no bucket while the
 *  derivation counts one, so every metric read slides one column over and
 *  renders the next metric or `undefined` coerced to 0. Silently wrong
 *  NUMBERS, from a duplicated rule.
 *
 *  So `ChartResult.dimension_column_count` is authoritative. It is null only
 *  for a result cached before that field existed (3-hour TTL), where the
 *  old derivation stands in — the pre-existing behavior, never a silent 0,
 *  which would misread every dimensioned chart the previous deploy wrote. */
export function resolveDimensionCount(chart: ChartSpec, result: ChartResult): number {
  return result.dimension_column_count ?? dimensionColumnCount(chart.query);
}

/** A cell value coerced to a finite number. `ChartResult.rows` is
 *  `unknown[][]` on the wire (BigQuery can hand back a numeric string, a
 *  real number, or null for a genuinely absent aggregate) — this is the one
 *  place every adapter function funnels a cell through, so a non-numeric or
 *  missing value degrades to 0 rather than propagating `NaN` into a chart
 *  primitive that assumes real numbers throughout. */
function toNumber(value: unknown): number {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string") {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

/** Raised when a chart's declared query shape has no supported adapter path
 *  for the primitive being fed. The caller (the dashboard renderer) catches
 *  this per-chart and renders that one chart's error state rather than
 *  crashing the page (§6/§7 of the design doc — one bad chart never blocks
 *  its siblings). */
export class UnsupportedChartShape extends Error {}

/** `LineAreaChart`/`BarChart` series, one per declared metric, index-aligned
 *  to `bucketsFromResult`'s buckets. The series `key` is `metric-${index}`, not
 *  the label: `ChartSeries.key` wants an identity a label edit cannot recolor,
 *  and `MetricRef` has no id, so declared POSITION is the stable substitute.
 *
 *  Throws `UnsupportedChartShape` for any groupby chart. A one-groupby,
 *  one-metric pivot goes through `pivotFromResult` (`lib/pivot.ts`) instead.
 *  No other groupby shape has a validated reshape. */
export function seriesFromResult(chart: ChartSpec, result: ChartResult): ChartSeries[] {
  const groupby = chart.query.groupby ?? [];
  if (groupby.length > 0) {
    throw new UnsupportedChartShape(
      `${chart.slice_name}: groupby-driven multi-series (a pivot) isn't supported by ` +
        "seriesFromResult, use pivotFromResult (lib/pivot.ts) for that shape",
    );
  }
  const metrics = metricRefs(chart.query);
  const dimCount = resolveDimensionCount(chart, result);
  return metrics.map((metric, index) => ({
    key: `metric-${index}`,
    label: metric.label,
    values: result.rows.map((row) => toNumber(row[dimCount + index])),
  }));
}

/** The x-axis bucket labels for a timeseries chart, one per row, or `[]` for
 *  a chart with no bucket/x_axis column at all (a plain grouped/single-row
 *  aggregate has nothing to bucket by). The bucket column is always the
 *  LAST dimension column (`compiler.py` appends it after every `groupby`
 *  entry), so its index is `groupby.length`, never `dimensionColumnCount`
 *  (which also counts past it into the metrics). */
export function bucketsFromResult(chart: ChartSpec, result: ChartResult): string[] {
  // Same authoritative count as every metric read, not `hasBucketColumn`:
  // that predicate mirrors half the compiler's condition, so on a
  // grain-without-temporal-column chart it would return the METRIC column's
  // values as if they were bucket labels.
  const bucketIndex = (chart.query.groupby ?? []).length;
  if (resolveDimensionCount(chart, result) <= bucketIndex) return [];
  return result.rows.map((row) => {
    const cell = row[bucketIndex];
    return cell === null || cell === undefined ? "" : String(cell);
  });
}

/** `StatTile` props for `big_number_total`/`big_number` (design doc §0.3):
 *  `big_number_total` has no bucket column and returns one row — `value` is
 *  that row's metric, no `spark`. `big_number` (the trend variant,
 *  `BigNumberDisplay.show_trend_line`) has a time-bucketed multi-row result
 *  — the headline `value` is the LAST row (the most recent bucket) and
 *  `spark` is the whole metric column across every row, oldest first (the
 *  compiled query has no explicit ORDER BY for this shape, so this trusts
 *  BigQuery's `GROUP BY DATE_TRUNC(...)` to come back in bucket order, which
 *  is the same assumption `TimeSpec`'s own trend-chart contract makes). */
export function statValueFromResult(
  chart: ChartSpec,
  result: ChartResult,
): { value: number; spark?: number[] } {
  const dimCount = resolveDimensionCount(chart, result);
  const values = result.rows.map((row) => toNumber(row[dimCount]));
  if (values.length === 0) return { value: 0 };
  const value = values[values.length - 1] as number;
  // A bucket exists iff the compiler emitted more dimensions than groupby
  // alone — derived from the authoritative count, not the half-mirrored
  // predicate, so the sparkline agrees with the value beside it.
  const hasBucket = dimCount > (chart.query.groupby ?? []).length;
  const spark = hasBucket && values.length > 1 ? values : undefined;
  return spark ? { value, spark } : { value };
}

/** `Gauge` props for `gauge_chart`: a ratio of `value` within
 *  `[min_val, max_val]`, clamped to `[0, 1]` for this function's own contract
 *  (Gauge re-clamps too). `chart.display` is cast to `GaugeDisplay` because
 *  `DisplaySpec` carries no discriminant on the wire. The viz_type-to-Display
 *  mapping is `_DISPLAY_FOR_VIZ` in `service/bi/core/spec.py`. Call only after
 *  branching on `chart.viz_type === "gauge_chart"`. */
export function gaugeInputFromResult(
  chart: ChartSpec,
  result: ChartResult,
): { ratio: number; value: number } {
  const display = chart.display as GaugeDisplay;
  const dimCount = resolveDimensionCount(chart, result);
  const value = result.rows.length > 0 ? toNumber(result.rows[0]?.[dimCount]) : 0;
  const span = display.max_val - display.min_val;
  const ratio = span !== 0 ? (value - display.min_val) / span : 0;
  return { ratio: Math.max(0, Math.min(ratio, 1)), value };
}

/** Status color for a gauge with `GaugeDisplay.intervals` bands. `intervals` is
 *  a comma-separated ascending list of fractions of `[min_val, max_val]`
 *  (Superset's convention, e.g. `"0.4,1"`). Only the BOUNDARIES are read:
 *  `interval_color_indices` point into Superset's `color_scheme` palette, which
 *  this repo does not import. Each boundary crossed escalates one step
 *  (success -> warning -> danger), capped at danger. No `intervals` (most gauges)
 *  returns the neutral accent token. */
export function gaugeStatusColor(ratio: number, display: GaugeDisplay): string {
  const raw = display.intervals?.trim();
  if (!raw) return "var(--rc-color-accent)";
  const boundaries = raw
    .split(",")
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);
  if (boundaries.length === 0) return "var(--rc-color-accent)";
  const ramp = ["var(--rc-color-success)", "var(--rc-color-warning)", "var(--rc-color-danger)"];
  let step = 0;
  for (const boundary of boundaries) {
    if (ratio >= boundary) step += 1;
  }
  return ramp[Math.min(step, ramp.length - 1)] as string;
}

/** `DonutChart` slices for `pie`: one slice per leading-dimension value (the
 *  Superset pie corpus groups by exactly one categorical dimension, but a
 *  grained pie's dimension is the time bucket instead), valued by the first
 *  declared metric. A pie whose result carries NO dimension column at all
 *  (rare — a single metric with nothing to slice by) degrades to one slice
 *  named after the metric, rather than an empty chart. `DonutChart` handles
 *  folding to the top 3 + Other and all color assignment — this function
 *  only extracts the raw category/value pairs. */
export function donutSlicesFromResult(chart: ChartSpec, result: ChartResult): DonutSlice[] {
  const metrics = metricRefs(chart.query);
  const metric = metrics[0];
  // The authoritative dimension count, NOT `groupby.length`: the compiler emits
  // a DATE_TRUNC bucket whenever a grain applies, for any viz_type, so a grained
  // pie with an empty `groupby` still has one leading label column (one slice
  // per bucket).
  const valueIndex = resolveDimensionCount(chart, result);
  if (valueIndex === 0) {
    const value = result.rows.length > 0 ? toNumber(result.rows[0]?.[valueIndex]) : 0;
    return metric ? [{ key: "metric-0", label: metric.label, value }] : [];
  }
  return result.rows.map((row, index) => {
    const cell = row[0];
    const label = cell === null || cell === undefined ? "Unknown" : String(cell);
    return { key: `${label}-${index}`, label, value: toNumber(row[valueIndex]) };
  });
}

/** `table` needs no adapter beyond a pass-through: `ChartResult.columns`
 *  already names every column in display order (raw mode: `all_columns`
 *  verbatim; aggregate mode: `groupby` then `metrics`, exactly this
 *  module's own `select_dims + select_metrics` order) — self-describing on
 *  the wire, unlike every other viz_type here. Kept as a named export for
 *  symmetry with the rest of this module rather than inlined at the call
 *  site, so every viz_type's adapter lives in one place. */
export function tableRowsFromResult(result: ChartResult): { columns: string[]; rows: unknown[][] } {
  return { columns: result.columns, rows: result.rows };
}

/** Split a funnel chart's LONG result into the nodes and edges the shared
 *  `FunnelChart` takes.
 *
 *  A sankey needs both, but a chart binds to exactly ONE dataset, so the funnel
 *  dataset returns them stacked with a discriminator column and this puts them
 *  back. `FunnelDisplay` names every column, so unlike every other adapter in
 *  this module the lookup here is BY NAME, not positional. That is sound for
 *  exactly one reason: a funnel chart is raw-mode (`query.all_columns`), and a
 *  raw-mode result's `columns` names the selected columns verbatim, the same
 *  property `tableRowsFromResult` relies on. Do NOT switch a funnel chart to
 *  aggregate mode without revisiting this, because the compiler slugifies
 *  metric aliases and these names would stop resolving.
 *
 *  `node` and `edge` are the CONTRACT, not a convention: the row_kind COLUMN is
 *  named by the display block, its two VALUES are fixed by the viz type (see
 *  `FunnelDisplay`). So an unrecognised kind raises rather than being skipped —
 *  dropping it silently turns a dataset that spells them differently into an
 *  empty funnel, which reads as "nothing happened here" rather than "this chart
 *  cannot be drawn". A missing REQUIRED column raises for the same reason, and
 *  names the column. */
export function funnelInputFromResult(
  display: FunnelDisplay,
  result: ChartResult,
): { stages: FunnelStageDatum[]; transitions: FunnelTransitionDatum[] } {
  const required = (name: string): number => {
    const at = result.columns.indexOf(name);
    if (at === -1) {
      throw new UnsupportedChartShape(
        `The funnel dataset has no column "${name}". It returns: ${result.columns.join(", ")}.`,
      );
    }
    return at;
  };
  // NULL in the display block means "this funnel has no secondary series",
  // which is a legitimate configuration. A NAMED column that the dataset does
  // not return is a different thing entirely: the author asked for a series and
  // the data cannot supply it, and returning null here would draw a guest
  // series of zero — a FALSE number rather than a missing one, which is exactly
  // what this app withholds rather than caveats (see frontend/bi/CLAUDE.md).
  const optional = (name: string | null | undefined): number | null =>
    name === null || name === undefined ? null : required(name);

  const kindAt = required(display.row_kind_column);
  const stageKeyAt = required(display.stage_key_column);
  const stageLabelAt = required(display.stage_label_column);
  const stageOrderAt = required(display.stage_order_column);
  const reachedAt = required(display.reached_column);
  const stalledAt = required(display.stalled_column);
  const reachedSecondaryAt = optional(display.secondary_reached_column);
  const stalledSecondaryAt = optional(display.secondary_stalled_column);
  const fromAt = required(display.edge_from_column);
  const toAt = required(display.edge_to_column);
  const edgeCountAt = required(display.edge_count_column);
  const edgeSecondaryAt = optional(display.edge_secondary_count_column);

  const ordered: (FunnelStageDatum & { order: number })[] = [];
  const transitions: FunnelTransitionDatum[] = [];

  for (const row of result.rows) {
    const kind = String(row[kindAt] ?? "");
    if (kind === "node") {
      ordered.push({
        key: String(row[stageKeyAt] ?? ""),
        label: String(row[stageLabelAt] ?? ""),
        order: toNumber(row[stageOrderAt]),
        reached: toNumber(row[reachedAt]),
        guestReached: reachedSecondaryAt === null ? 0 : toNumber(row[reachedSecondaryAt]),
        stalled: toNumber(row[stalledAt]),
        guestStalled: stalledSecondaryAt === null ? 0 : toNumber(row[stalledSecondaryAt]),
      });
    } else if (kind === "edge") {
      transitions.push({
        fromKey: String(row[fromAt] ?? ""),
        toKey: String(row[toAt] ?? ""),
        count: toNumber(row[edgeCountAt]),
        guestCount: edgeSecondaryAt === null ? 0 : toNumber(row[edgeSecondaryAt]),
      });
    } else {
      throw new UnsupportedChartShape(
        `The funnel dataset's "${display.row_kind_column}" column contains ` +
          `"${kind}", but a funnel row is either "node" or "edge".`,
      );
    }
  }

  // Every column resolved and every row understood, and still no bars: the
  // dataset returned edges only, or nothing at all. Withheld rather than drawn,
  // because an empty funnel is indistinguishable from a real cohort of zero.
  if (ordered.length === 0) {
    throw new UnsupportedChartShape(
      `The funnel dataset returned no "node" rows, so there are no stages to draw.`,
    );
  }

  // The DECLARED stage order, not the row order: BigQuery promises no ordering,
  // and a funnel drawn in arrival order is not a funnel.
  ordered.sort((a, b) => a.order - b.order);
  const stages = ordered.map(({ order: _order, ...stage }) => stage);

  if (display.outcome_stage_keys?.length) {
    try {
      layoutFunnelFlow(stages, transitions, display.outcome_stage_keys);
    } catch (error) {
      throw new UnsupportedChartShape(error instanceof Error ? error.message : String(error));
    }
    return { stages, transitions };
  }

  // The layout draws ONE column-skipping bypass, so a dataset with two (or an
  // edge naming a stage that is not in the result) would put projects on screen
  // nowhere at all: no ribbon, no drop, no gap to notice. That is a quietly
  // incomplete chart, which this app withholds rather than caveats.
  //
  // `resolveEdges` is the geometry's OWN drawable-edge rule. Reuse it; do not
  // re-implement "what counts as drawable" here.
  const { undrawnTransitions } = resolveEdges(stages, transitions);
  if (undrawnTransitions.length > 0) {
    const named = undrawnTransitions.map((t) => `${t.fromKey} to ${t.toKey}`).join(", ");
    throw new UnsupportedChartShape(
      `This funnel has ${undrawnTransitions.length} transition(s) the chart cannot draw ` +
        `(${named}). Only one column-skipping step can be shown, so rendering would hide ` +
        `those projects entirely.`,
    );
  }
  return { stages, transitions };
}
